<template>
  <div class="metric-page">
    <CRow>
      <CCol lg="6" class="mb-4">
        <CCard class="metric-card">
          <CCardBody>
            <div class="metric-card__header">
              <div>
                <div class="metric-card__eyebrow">Zadnjih 7 dana</div>
                <h3 class="metric-card__title">Trajanje sna</h3>
              </div>
              <div class="metric-card__stats">
                <div>
                  <span class="metric-card__stats-label">Danas</span>
                  <strong>{{ todaySleep }}</strong>
                </div>
                <div>
                  <span class="metric-card__stats-label">Prosjek</span>
                  <strong>{{ averageSleepLastWeek }}</strong>
                </div>
              </div>
            </div>
            <div class="metric-card__chart">
              <CChartBar class="metric-card__chart-canvas" :datasets="lastWeekChart.datasets" :options="chartOptions" :labels="lastWeekChart.labels" />
            </div>
          </CCardBody>
        </CCard>
      </CCol>

      <CCol lg="6" class="mb-4">
        <CCard class="metric-card">
          <CCardBody>
            <div class="metric-card__header">
              <div>
                <div class="metric-card__eyebrow">Povijest</div>
                <h3 class="metric-card__title">Svi zapisi</h3>
              </div>
              <div class="metric-card__stats">
                <div>
                  <span class="metric-card__stats-label">Minimum</span>
                  <strong>{{ minSleep }} h</strong>
                </div>
                <div>
                  <span class="metric-card__stats-label">Prosjek</span>
                  <strong>{{ averageAllSleep }}</strong>
                </div>
              </div>
            </div>
            <div class="metric-card__chart">
              <CChartBar class="metric-card__chart-canvas" :datasets="allTimeChart.datasets" :options="chartOptions" :labels="allTimeChart.labels" />
            </div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <MetricHistoryTable
      :entries="$store.state.sleep"
      value-title="San"
      unit="h"
      :decimals="1"
      increase-is-good
    />
  </div>
</template>

<script>
import { CChartBar } from "@coreui/vue-chartjs";
import moment from "moment";
import MetricHistoryTable from "./MetricHistoryTable";

const currentYear = new Date().getFullYear();
const getChartXAxisDateFormat = (date) => moment(date).format("DD.MM" + (moment(date).format("YYYY") == currentYear ? "" : ".YY"));

const getDaysBetweenDates = (from, to) => {
  const dates = [];
  while (from.isSameOrBefore(to)) {
    dates.push(from.toISOString().substring(0, 10));
    from = moment(from).add(1, "days");
  }
  return dates;
};

const buildSleepTimeline = (sleepData) => {
  if (!sleepData.length) {
    return [];
  }

  const fromDate = moment(sleepData[0].date);
  const toDate = moment().add(1, "days");

  return getDaysBetweenDates(fromDate, toDate).map((date) => {
    return {
      date,
      value:
        sleepData
          .filter((entry) => date === entry.date)
          .map((entry) => entry.value)
          .pop() ?? 0,
    };
  });
};

const getAverage = (entries) => entries.map((entry) => entry.value).reduce((sum, value) => sum + value, 0) / entries.filter((entry) => entry.value > 0).length || 0;

const buildBarDataset = (entries, threshold) => ({
  labels: entries.map((entry) => getChartXAxisDateFormat(entry.date)),
  datasets: [
    {
      label: "Sleep",
      backgroundColor: entries.map((entry) => (entry.value < threshold ? "#dc2626" : "#0f766e")),
      borderRadius: 6,
      barPercentage: 0.72,
      categoryPercentage: 0.74,
      data: entries.map((entry) => entry.value),
    },
    {
      type: "line",
      label: "Minimum",
      data: entries.map(() => threshold),
      borderColor: "#d97706",
      borderDash: [6, 6],
      borderWidth: 2,
      pointRadius: 0,
      fill: false,
    },
  ],
});

export default {
  components: { CChartBar, MetricHistoryTable },
  data() {
    return {
      todaySleep: "0 h",
      averageSleepLastWeek: "0 h",
      averageAllSleep: "0 h",
      allTimeChart: { labels: [], datasets: [] },
      lastWeekChart: { labels: [], datasets: [] },
    };
  },
  computed: {
    minSleep() {
      return this.$store.state.minSleep;
    },
    chartOptions() {
      return {
        maintainAspectRatio: false,
        legend: { display: false },
        tooltips: {
          mode: "index",
          intersect: false,
          backgroundColor: "#0f172a",
          titleFontColor: "#f8fafc",
          bodyFontColor: "#e2e8f0",
          xPadding: 12,
          yPadding: 10,
          callbacks: {
            label: (tooltipItem, data) => {
              const dataset = data.datasets[tooltipItem.datasetIndex];
              return dataset.type === "line" ? `Minimum: ${tooltipItem.value} h` : `Sleep: ${tooltipItem.value} h`;
            },
          },
        },
        scales: {
          xAxes: [
            {
              gridLines: { display: false, drawBorder: false },
              ticks: { fontColor: "#94a3b8", maxTicksLimit: 8 },
            },
          ],
          yAxes: [
            {
              gridLines: { color: "#e8eef5", drawBorder: false },
              ticks: {
                min: 0,
                maxTicksLimit: 6,
                fontColor: "#94a3b8",
                callback: (value) => `${value} h`,
              },
            },
          ],
        },
      };
    },
  },
  mounted() {
    const sleep = buildSleepTimeline(this.$store.state.sleep);
    const todayEntry = sleep[sleep.length - 1] || { value: 0 };
    const historicalSleep = sleep;
    const lastWeekSleep = historicalSleep.slice(Math.max(historicalSleep.length - 7, 0));

    this.todaySleep = `${todayEntry.value} h`;
    this.averageAllSleep = `${(Math.round(getAverage(historicalSleep) * 10) / 10 || 0).toFixed(1)} h`;
    this.averageSleepLastWeek = `${(Math.round(getAverage(lastWeekSleep) * 10) / 10 || 0).toFixed(1)} h`;
    this.allTimeChart = buildBarDataset(historicalSleep, this.minSleep);
    this.lastWeekChart = buildBarDataset(lastWeekSleep, this.minSleep);
  },
};
</script>

<style scoped>
.metric-page {
  display: grid;
  gap: 1.5rem;
}

.metric-card {
  height: 100%;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  box-shadow: none;
}

.metric-card .card-body {
  display: flex;
  flex-direction: column;
  min-height: 380px;
  padding: 1.5rem;
}

.metric-card__chart {
  position: relative;
  flex: 0 0 auto;
  height: clamp(220px, 28vh, 260px);
  width: 100%;
  min-width: 0;
  overflow: hidden;
}

.metric-card__chart-canvas {
  height: 100%;
  width: 100%;
  min-width: 0;
}

.metric-card__chart-canvas /deep/ canvas {
  height: 100% !important;
  width: 100% !important;
  max-width: 100%;
  display: block;
}

.metric-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.metric-card__eyebrow {
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.metric-card__title {
  margin: 0.35rem 0 0;
  color: #0f172a;
  font-size: 1.15rem;
  font-weight: 700;
}

.metric-card__stats {
  display: flex;
  gap: 1.5rem;
  text-align: right;
}

.metric-card__stats-label {
  display: block;
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 600;
}

.metric-card__stats strong {
  color: #0f172a;
  font-size: 1.05rem;
  line-height: 1.2;
}

@media (max-width: 767px) {
  .metric-card {
    border-radius: 14px;
  }

  .metric-card .card-body {
    min-height: 340px;
    padding: 1rem;
  }

  .metric-card__chart {
    height: 210px;
  }

  .metric-card__header {
    flex-direction: column;
    margin-bottom: 1rem;
  }

  .metric-card__stats {
    width: 100%;
    justify-content: space-between;
    text-align: left;
  }

  .metric-card__title {
    font-size: 1.05rem;
  }
}
</style>
